import {Close} from '@mui/icons-material';
import {Box, Button, IconButton, MenuItem, TextField} from '@mui/material';
import {useOptimisticSearchParams} from 'nuqs/adapters/react-router/v7';
import {NavLink} from 'react-router';
import SliderFilter from '../panels/search/filters/SliderFilter.jsx';
import {INTERPOLATOR, MOSAICKING_ORDER} from '@src/constants/analyze.ts';
import useVisualizationParams, {
  EFFECTS_URL_KEY_MAP,
  RangeTuple,
  SETTINGS_URL_KEY_MAP,
} from '@src/hooks/useVisualizationParams.ts';

const MOSAICKING_ORDER_LABELS = {
  [MOSAICKING_ORDER.mostRecent]: 'Most recent',
  [MOSAICKING_ORDER.leastRecent]: 'Least recent',
  [MOSAICKING_ORDER.leastCC]: 'Least cloud coverage',
} as const;

const INTERPOLATOR_LABELS = {
  [INTERPOLATOR.nearest]: 'Nearest',
  [INTERPOLATOR.bilinear]: 'Bilinear',
  [INTERPOLATOR.bicubic]: 'Bicubic',
  [INTERPOLATOR.lanczos]: 'Lanczos',
  [INTERPOLATOR.box]: 'Box',
} as const;

const COLOR_KEY_LABEL_MAP = {
  redRange: 'Red',
  greenRange: 'Green',
  blueRange: 'Blue',
} as const;

const sliderRootProps = {
  sx: {paddingBlock: 0},
};

export const SettingsAndEffectsPanel = () => {
  const searchParams = useOptimisticSearchParams();
  const searchParams2 = new URLSearchParams(searchParams);

  Object.values({...SETTINGS_URL_KEY_MAP, ...EFFECTS_URL_KEY_MAP}).forEach(
    key => searchParams2.delete(key),
  );
  const resetSearchParams = searchParams2.toString() ? `?${searchParams2}` : '';
  const backSearchParams = searchParams?.toString() ? `?${searchParams}` : '';

  const visualizationParams = useVisualizationParams();
  const {
    mosaickingOrder,
    downsampling,
    upsampling,
    gain,
    gamma,
    settingsEffectsApplies,
    setParams,
  } = visualizationParams;

  return (
    <>
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
        <Box sx={{display: 'flex', alignItems: 'center'}}>
          <Box
            component="h4"
            sx={{
              color: 'text.secondary',
              typography: 'overline',
              flex: 1,
              m: 0,
            }}
          >
            Visualization Settings & Effects
          </Box>
          <IconButton
            component={NavLink}
            size="small"
            to={`..${backSearchParams}`}
            title="Close"
          >
            <Close fontSize="small" color="action" sx={{opacity: 0.5}} />
          </IconButton>
        </Box>
        <TextField
          select
          size="small"
          label="Mosaicking order"
          name="mosaicking"
          value={mosaickingOrder ?? ''}
          onChange={e => {
            setParams({
              mosaickingOrder: e.target.value as MOSAICKING_ORDER,
            });
          }}
          sx={theme => ({
            backgroundColor: 'background.paper',
            backgroundImage: theme.vars?.overlays[1],
          })}
        >
          {Object.entries(MOSAICKING_ORDER_LABELS).map(([key, label]) => (
            <MenuItem key={key} value={key}>
              {label}
            </MenuItem>
          ))}
        </TextField>
        <Box sx={{display: 'flex', justifyContent: 'space-between', gap: 2}}>
          <TextField
            select
            size="small"
            label="Upsampling"
            name="upsampling"
            value={upsampling ?? ''}
            onChange={e => {
              setParams({
                upsampling: e.target.value as INTERPOLATOR,
              });
            }}
            sx={theme => ({
              backgroundColor: 'background.paper',
              backgroundImage: theme.vars?.overlays[1],
              flexBasis: '50%',
            })}
          >
            {Object.entries(INTERPOLATOR_LABELS).map(([key, label]) => (
              <MenuItem key={key} value={key}>
                {label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Downsampling"
            name="downsampling"
            value={downsampling ?? ''}
            onChange={e => {
              setParams({
                downsampling: e.target.value as INTERPOLATOR,
              });
            }}
            defaultValue=""
            sx={theme => ({
              backgroundColor: 'background.paper',
              backgroundImage: theme.vars?.overlays[1],
              flexBasis: '50%',
            })}
          >
            {Object.entries(INTERPOLATOR_LABELS).map(([key, label]) => (
              <MenuItem key={key} value={key}>
                {label}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <SliderFilter
          values={[gain]}
          name="Gain"
          min={0}
          max={10}
          unit=""
          onChangeComplete={([gain]: [number]) => {
            setParams({gain});
          }}
          rootProps={sliderRootProps}
        />
        <SliderFilter
          values={[gamma]}
          name="Gamma"
          min={0.1}
          max={5}
          unit=""
          onChangeComplete={([gamma]: [number]) => {
            setParams({gamma});
          }}
          rootProps={sliderRootProps}
        />
        {Object.entries(COLOR_KEY_LABEL_MAP).map(([key, label]) => {
          return (
            <SliderFilter
              key={key}
              values={
                visualizationParams[key as keyof typeof COLOR_KEY_LABEL_MAP]
              }
              name={label}
              min={0}
              max={1}
              unit=""
              onChangeComplete={(range: RangeTuple) => {
                setParams({[key]: range});
              }}
              rootProps={sliderRootProps}
            />
          );
        })}
        <Button
          variant="contained"
          color="secondary"
          fullWidth
          disabled={!settingsEffectsApplies}
          role="button"
          component={NavLink}
          to={resetSearchParams}
        >
          Reset
        </Button>
      </Box>
    </>
  );
};

export default SettingsAndEffectsPanel;
